import { useEffect, useState } from "react"

import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp"

import { StickyFormActions } from "@/components/account/layout/StickyFormActions"
import { Button, LoadingButton } from "@/components/ui/button"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp"
import { RESEND_SECONDS } from "@/lib/constants"

const CODE_LENGTH = 6

type CodeEntryFormProps = {
  email: string
  onSubmit: (code: string) => void
  onResend: () => void
  isSubmitting?: boolean
  submitLabel?: string
  error?: string | null
}

export function CodeEntryForm({
  email,
  onSubmit,
  onResend,
  isSubmitting = false,
  submitLabel = "Verify",
  error,
}: CodeEntryFormProps) {
  const [code, setCode] = useState("")
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS)

  useEffect(() => {
    if (secondsLeft <= 0) {
      return
    }
    const timer = setInterval(() => {
      setSecondsLeft((seconds) => (seconds <= 1 ? 0 : seconds - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [secondsLeft])

  const isComplete = code.length === CODE_LENGTH

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (isComplete) {
      onSubmit(code)
    }
  }

  function handleResend() {
    onResend()
    setSecondsLeft(RESEND_SECONDS)
    setCode("")
  }

  return (
    <form className="flex flex-1 flex-col" onSubmit={handleSubmit}>
      <div className="flex flex-1 flex-col gap-4">
        <p className="text-center text-neutral-600">
          Enter the code we sent to{" "}
          <span className="font-medium text-neutral-800">{email}</span>.
        </p>
        <p className="text-center text-sm text-neutral-500">
          It can take up to {RESEND_SECONDS} seconds to arrive. If you request a
          new code, only the latest one will work.
        </p>

        <InputOTP
          maxLength={CODE_LENGTH}
          pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
          value={code}
          onChange={(value) => setCode(value.toUpperCase())}
          autoFocus
          disabled={isSubmitting}
          aria-label="Verification code"
          aria-invalid={!!error}
          containerClassName="justify-center"
        >
          <InputOTPGroup className="gap-2">
            {Array.from({ length: CODE_LENGTH }).map((_, index) => (
              <InputOTPSlot key={index} index={index} aria-invalid={!!error} />
            ))}
          </InputOTPGroup>
        </InputOTP>

        {error && (
          <p className="text-destructive text-center text-sm font-medium">
            {error}
          </p>
        )}

        <StickyFormActions>
          {isSubmitting ? (
            <LoadingButton>{submitLabel}</LoadingButton>
          ) : (
            <Button type="submit" disabled={!isComplete}>
              {submitLabel}
            </Button>
          )}

          <div className="text-center">
            <Button
              variant="link"
              type="button"
              className="p-0 text-neutral-600 hover:text-neutral-800"
              disabled={secondsLeft > 0}
              onClick={handleResend}
            >
              {secondsLeft > 0
                ? `Resend code in ${secondsLeft}s`
                : "Resend code"}
            </Button>
          </div>
        </StickyFormActions>
      </div>
    </form>
  )
}
